/**
 * Compact inline fund picker shown at the top of each tab view.
 * - mode="single" → radio-style, writes to selectedFundId
 * - mode="multi"  → checkbox-style, writes to selectedFundIds
 *
 * State sync: multi mode initialises from selectedFundId when selectedFundIds is empty
 * so switching tabs doesn't lose the current fund context.
 */
import { useMemo, useState } from "react";
import { Input, Checkbox } from "@heroui/react";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import { sortPeriods } from "../model/utils";

interface FundPickerBarProps {
  mode: "single" | "multi";
}

export default function FundPickerBar({ mode }: FundPickerBarProps) {
  const {
    funds,
    docMeta,
    selectedFundId,
    setSelectedFundId,
    selectedFundIds,
    setSelectedFundIds,
  } = useS2DashboardContext();

  const [search, setSearch] = useState("");

  // For multi mode: if nothing explicitly selected yet, treat selectedFundId as selected
  const effectiveMultiIds = useMemo(() => {
    if (mode !== "multi") return [];
    if (selectedFundIds.length > 0) return selectedFundIds;
    return selectedFundId ? [selectedFundId] : [];
  }, [mode, selectedFundIds, selectedFundId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return funds;
    return funds.filter(
      (f) =>
        (f.fund_id || "").toLowerCase().includes(q) ||
        (f.fund_name || "").toLowerCase().includes(q),
    );
  }, [funds, search]);

  const isSelected = (id: string) => {
    if (mode === "single") return selectedFundId === id;
    return effectiveMultiIds.includes(id);
  };

  const handleToggle = (id: string) => {
    if (mode === "single") {
      setSelectedFundId(selectedFundId === id ? null : id);
    } else {
      // Initialize from current single selection if multi list is empty
      const current =
        selectedFundIds.length > 0
          ? selectedFundIds
          : selectedFundId
            ? [selectedFundId]
            : [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      setSelectedFundIds(next);
      setSelectedFundId(next.length > 0 ? next[next.length - 1] : null);
    }
  };

  const allFundIds = funds.map((f) => f.fund_id);
  const allSelected =
    mode === "multi" &&
    funds.length > 0 &&
    allFundIds.every((id) => effectiveMultiIds.includes(id));
  const someSelected =
    mode === "multi" && effectiveMultiIds.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedFundIds([]);
      setSelectedFundId(null);
    } else {
      setSelectedFundIds(allFundIds);
      setSelectedFundId(allFundIds[allFundIds.length - 1]);
    }
  };

  const selectedCount =
    mode === "single" ? (selectedFundId ? 1 : 0) : effectiveMultiIds.length;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm mb-4">
      {/* Header row */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
          {mode === "single" ? "Fund" : "Funds"}
        </span>

        <div className="w-40 shrink-0">
          <Input
            size="sm"
            placeholder="Search…"
            value={search}
            onValueChange={setSearch}
            startContent={
              <svg
                className="h-3 w-3 text-gray-400 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
            }
            classNames={{
              input: "text-xs",
              inputWrapper: "h-6 min-h-6 bg-gray-50 shadow-none",
            }}
          />
        </div>

        {mode === "multi" && (
          <Checkbox
            isSelected={allSelected}
            isIndeterminate={someSelected}
            size="sm"
            onChange={handleSelectAll}
            classNames={{ label: "text-xs text-gray-500 font-medium" }}
          >
            All
          </Checkbox>
        )}

        <span className="ml-auto text-[11px] text-gray-400 shrink-0">
          {selectedCount > 0
            ? `${selectedCount} selected`
            : mode === "single"
              ? "Select a fund"
              : "None selected"}
        </span>
      </div>

      {/* Fund chips — horizontal scroll */}
      <div className="flex items-center gap-2 overflow-x-auto px-4 py-2.5">
        {filtered.length === 0 && (
          <span className="text-xs text-gray-400 py-1">No funds found</span>
        )}
        {filtered.map((fund) => {
          // Find the most recent period across all doc_ids using semantic sort
          const allPeriods = fund.doc_ids
            .map((d) => docMeta[d]?.reporting_period)
            .filter(Boolean) as string[];
          const latestPeriod = sortPeriods(allPeriods).slice(-1)[0] ?? "";
          const selected = isSelected(fund.fund_id);

          return (
            <button
              key={fund.fund_id}
              onClick={() => handleToggle(fund.fund_id)}
              className={[
                "flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-left transition-all",
                selected
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-gray-200 bg-gray-50 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50",
              ].join(" ")}
            >
              {/* Checkbox / radio indicator */}
              {mode === "multi" ? (
                <div
                  className={`h-3.5 w-3.5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                    selected
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {selected && (
                    <svg
                      className="h-2 w-2 text-white"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              ) : (
                <div
                  className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                    selected ? "border-blue-500" : "border-gray-300"
                  }`}
                >
                  {selected && (
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                  )}
                </div>
              )}

              <div className="min-w-0">
                <p
                  className="truncate text-xs font-semibold max-w-[140px]"
                  title={fund.fund_name || fund.fund_id}
                >
                  {fund.fund_name || fund.fund_id}
                </p>
                {latestPeriod && (
                  <p className="text-[10px] opacity-60 leading-tight">
                    {latestPeriod}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
